import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/api/client";
import type { components } from "@/api/types";

type RetentionPolicyRead = components["schemas"]["RetentionPolicyRead"];
type RetentionPolicyCreate = components["schemas"]["RetentionPolicyCreate"];
type LegalHoldRead = components["schemas"]["LegalHoldRead"];
type LegalHoldCreate = components["schemas"]["LegalHoldCreate"];
type PurgePreviewRead = components["schemas"]["PurgePreviewRead"];

export function useRetentionPolicies() {
  return useQuery({
    queryKey: ["retention-policies"],
    queryFn: () => api.get<RetentionPolicyRead[]>("/retention/policies"),
  });
}

export function useUpsertRetentionPolicy() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: RetentionPolicyCreate) =>
      api.put<RetentionPolicyRead>("/retention/policies", body),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["retention-policies"] }),
  });
}

export function useLegalHolds() {
  return useQuery({
    queryKey: ["legal-holds"],
    queryFn: () => api.get<LegalHoldRead[]>("/retention/legal-holds"),
  });
}

export function usePlaceLegalHold() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: LegalHoldCreate) =>
      api.post<LegalHoldRead>("/retention/legal-holds", body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["legal-holds"] }),
  });
}

export function useReleaseLegalHold() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (holdId: string) =>
      api.post<LegalHoldRead>(`/retention/legal-holds/${holdId}/release`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["legal-holds"] }),
  });
}

/**
 * Preview-only. The backend exposes no execute-purge route — destructive
 * deletion stays disabled until backups are restore-tested. A dry run counts
 * candidates and writes an immutable `purge_run` audit row; it never deletes.
 */
export function usePurgeDryRun() {
  return useMutation({
    mutationFn: (dataClass: string) =>
      api.post<PurgePreviewRead>("/retention/dry-run", {
        data_class: dataClass,
      }),
  });
}
